import { useState, useEffect } from 'react';
import { calculatePrice } from '../data/campers';
import { createClient } from 'contentful';

// Create a Contentful client using environment variables
const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN
});

export function useCamper(slug) {
  const [camper, setCamper] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setIsLoading(false);
      return;
    }

    client.getEntries({
      content_type: 'camper',
      'fields.slug': slug
    })
    .then(response => {
      if (response.items.length > 0) {
        const item = response.items[0];
        const mappedCamper = {
          slug: item.fields.slug,
          name: item.fields.name,
          shortDescription: item.fields.shortDescription,
          mainImage: item.fields.mainImage,
          galleryImages: item.fields.galleryImages || [],
          highlights: (item.fields.highlights || []).map((highlight) => ({
            icon: highlight.fields.icon,
            text: highlight.fields.text,
            optional: highlight.fields.optional || false
          })),
          technicalSpecs: {
            model: item.fields.model,
            power: item.fields.power,
            year: item.fields.year,
            fuel: item.fields.fuel
          },
          pricing: {
            highSeason: item.fields.highSeasonPricing || [],
            midSeason: item.fields.midSeasonPricing || [],
            lowSeason: item.fields.lowSeasonPricing || []
          },
          features: {
            exterior: item.fields.exteriorFeatures || [],
            interior: item.fields.interiorFeatures || [],
            comfort: item.fields.comfortFeatures || []
          }
        };
        setCamper(mappedCamper);
      } else {
        setCamper(null);
      }
      setIsLoading(false);
    })
    .catch(err => {
      console.error('Error fetching camper from Contentful:', err);
      setError(err.message);
      setIsLoading(false);
    });
  }, [slug]);

  return {
    camper,
    calculatePrice: (startDate, endDate) => camper ? calculatePrice(camper, startDate, endDate) : 0,
    isLoading,
    error
  };
}

export const useCampersList = () => {
  const [campers, setCampers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    client.getEntries({ content_type: 'camper' })
      .then((response) => {
        const mappedCampers = response.items.map((item) => {
          return {
            slug: item.fields.slug,
            name: item.fields.name,
            shortDescription: item.fields.shortDescription,
            mainImage: item.fields.mainImage,
            highlights: (item.fields.highlights || []).map((highlight) => ({
              icon: highlight.fields.icon,
              text: highlight.fields.text,
              optional: highlight.fields.optional || false
            }))
          };
        });
        setCampers(mappedCampers);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching campers from Contentful:', error);
        setIsLoading(false);
      });
  }, []);

  return { campers, isLoading };
};

// Hook to get general pricing information from Contentful
// Gets pricing from the first camper (assuming all campers have the same general pricing)
export const useGeneralPricing = () => {
  const [generalPricing, setGeneralPricing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch all campers to get pricing information
    client.getEntries({ content_type: 'camper' })
      .then((response) => {
        if (response.items.length > 0) {
          // Get pricing from the first camper (assuming all have same pricing)
          const firstCamper = response.items[0];
          const pricing = {
            highSeason: firstCamper.fields.highSeasonPricing || [],
            midSeason: firstCamper.fields.midSeasonPricing || [],
            lowSeason: firstCamper.fields.lowSeasonPricing || []
          };
          setGeneralPricing(pricing);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching general pricing from Contentful:', err);
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  return { generalPricing, isLoading, error };
};

// Optionally, if you need other helper functions, you may add them below. 